/**
 * The `postMessage` protocol between the parent application and the agent
 * script injected into every prototype HTML file (spec R4).
 *
 * Both sides always send with an explicit `targetOrigin` and always validate
 * `event.origin` against the configured sandbox origin before trusting a
 * message. The guards below are the second half of that check: they narrow an
 * `unknown` payload to a known message shape.
 *
 * Every message is discriminated on `type`.
 */
/* -------------------------------------------------------------------------- */
/* Type guards                                                                */
/* -------------------------------------------------------------------------- */
/** Every `type` value the parent may send. */
export const ATLAS_PARENT_MESSAGE_TYPES = [
    'atlas:set-mode',
    'atlas:render-pins',
    'atlas:highlight-pin',
    'atlas:scroll-to-pin',
    'atlas:navigate',
    'atlas:replay',
    'atlas:prototype-access-refreshed',
];
/** Every `type` value the agent may send. */
export const ATLAS_AGENT_MESSAGE_TYPES = [
    'atlas:ready',
    'atlas:navigated',
    'atlas:pin-candidate',
    'atlas:pin-clicked',
    'atlas:pin-positions',
    'atlas:replay-result',
    'atlas:prototype-access-expired',
    'atlas:error',
];
function hasStringType(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'type' in value &&
        typeof value.type === 'string');
}
/** Narrows an untrusted payload to a message the parent is allowed to send. */
export function isAtlasParentMessage(value) {
    return (hasStringType(value) && ATLAS_PARENT_MESSAGE_TYPES.includes(value.type));
}
/** Narrows an untrusted payload to a message the agent is allowed to send. */
export function isAtlasAgentMessage(value) {
    return (hasStringType(value) && ATLAS_AGENT_MESSAGE_TYPES.includes(value.type));
}
/** Narrows an untrusted payload to any Atlas protocol message. */
export function isAtlasMessage(value) {
    return isAtlasParentMessage(value) || isAtlasAgentMessage(value);
}
/**
 * Narrows an untrusted payload to one specific message.
 *
 * ```ts
 * if (isAtlasMessageOfType(event.data, 'atlas:pin-candidate')) {
 *   event.data.selector; // AtlasPinCandidateMessage
 * }
 * ```
 */
export function isAtlasMessageOfType(value, type) {
    return isAtlasMessage(value) && value.type === type;
}
//# sourceMappingURL=agent-protocol.js.map